import {
  formatActivityCategory,
  getActivityName,
} from '../../services/activityService';
import { getTrainingClassLabel } from '../../services/trainingClassService';
import { parseSessionDate } from '../homePageUtils';

export const durationOptions = [
  { value: '14d', label: 'Past 2 weeks', days: 14 },
  { value: '30d', label: 'Past 30 days', days: 30 },
  { value: '90d', label: 'Past 90 days', days: 90 },
  { value: '1y', label: 'Past year', days: 365 },
  { value: 'all', label: 'All time', days: null },
];

const monthlyDisabledDurations = new Set(['14d', '30d']);

export const isMonthlyGroupingDisabled = (duration) =>
  monthlyDisabledDurations.has(duration);

export const ACTIVITY_DONUT_LIMIT = 7;
export const ACTIVITY_CATEGORY_LINE_DEFAULT_LIMIT = 5;
export const CLASS_ACTIVITY_LINE_DEFAULT_LIMIT = 5;

export const createActivityCategoryMap = (activities = []) =>
  activities.reduce((acc, activity) => {
    acc[activity.id] = formatActivityCategory(activity.category) || 'Other';
    return acc;
  }, {});

export const filterSessionsByDuration = (sessions, duration) => {
  const selectedDuration = durationOptions.find((option) => option.value === duration);
  if (!selectedDuration?.days) {
    return sessions;
  }

  const cutoffDate = new Date();
  cutoffDate.setHours(0, 0, 0, 0);
  cutoffDate.setDate(cutoffDate.getDate() - selectedDuration.days);

  return sessions.filter((session) => {
    const parsedDate = parseSessionDate(session.sessionDate);
    return !!parsedDate && parsedDate >= cutoffDate;
  });
};

const getWeekStart = (date) => {
  const localDate = new Date(date);
  const day = localDate.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  localDate.setHours(0, 0, 0, 0);
  localDate.setDate(localDate.getDate() + diffToMonday);
  return localDate;
};

const formatDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getPeriodStart = (date, period) => {
  if (period === 'month') {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  return getWeekStart(date);
};

const formatPeriodLabel = (date, period) => {
  if (period === 'month') {
    return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  }

  const weekEndDate = new Date(date.getTime() + (6 * 24 * 60 * 60 * 1000));
  const startLabel = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const endLabel = weekEndDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${startLabel} - ${endLabel}`;
};

const addPeriod = (date, period) => {
  const nextDate = new Date(date);
  if (period === 'month') {
    nextDate.setMonth(nextDate.getMonth() + 1);
    return nextDate;
  }

  nextDate.setDate(nextDate.getDate() + 7);
  return nextDate;
};

export const createEmptyPeriods = (sessions, period) => {
  const datedSessions = sessions
    .map((session) => parseSessionDate(session.sessionDate))
    .filter(Boolean)
    .sort((a, b) => a.getTime() - b.getTime());

  if (datedSessions.length === 0) {
    return [];
  }

  const firstPeriod = getPeriodStart(datedSessions[0], period);
  const lastPeriod = getPeriodStart(datedSessions[datedSessions.length - 1], period);
  const periods = [];

  for (
    let currentPeriod = new Date(firstPeriod);
    currentPeriod <= lastPeriod;
    currentPeriod = addPeriod(currentPeriod, period)
  ) {
    periods.push({
      key: formatDateKey(currentPeriod),
      label: formatPeriodLabel(currentPeriod, period),
    });
  }

  return periods;
};

export const buildTotalTrainingByPeriod = (sessions, period) => {
  const periods = createEmptyPeriods(sessions, period);
  const totalsByPeriod = sessions.reduce((acc, session) => {
    const parsedDate = parseSessionDate(session.sessionDate);
    const duration = Number(session.durationMinutes) || 0;
    if (!parsedDate || duration <= 0) return acc;

    const periodKey = formatDateKey(getPeriodStart(parsedDate, period));
    acc[periodKey] = (acc[periodKey] || 0) + duration;
    return acc;
  }, {});

  return {
    labels: periods.map(({ label }) => label),
    values: periods.map(({ key }) => totalsByPeriod[key] || 0),
  };
};

export const buildClassTotals = (sessions, trainingClassesByName) => {
  const entries = Object.entries(sessions.reduce((acc, session) => {
    const sessionClass = session.sessionClass || 'other';
    const duration = Number(session.durationMinutes) || 0;
    if (duration <= 0) return acc;

    acc[sessionClass] = (acc[sessionClass] || 0) + duration;
    return acc;
  }, {})).sort((a, b) => b[1] - a[1]);

  return {
    labels: entries.map(([className]) => getTrainingClassLabel(trainingClassesByName, className)),
    values: entries.map(([, duration]) => duration),
  };
};

export const buildClassTrainingStackByPeriod = (sessions, period, trainingClassesByName) => {
  const periods = createEmptyPeriods(sessions, period);
  const periodKeys = periods.map(({ key }) => key);
  const totalsByClass = new Map();

  sessions.forEach((session) => {
    const parsedDate = parseSessionDate(session.sessionDate);
    const duration = Number(session.durationMinutes) || 0;
    if (!parsedDate || duration <= 0) return;

    const sessionClass = session.sessionClass || 'other';
    const classLabel = getTrainingClassLabel(trainingClassesByName, sessionClass);
    const periodKey = formatDateKey(getPeriodStart(parsedDate, period));

    if (!totalsByClass.has(classLabel)) {
      totalsByClass.set(classLabel, periodKeys.reduce((acc, key) => {
        acc[key] = 0;
        return acc;
      }, {}));
    }

    const classTotals = totalsByClass.get(classLabel);
    classTotals[periodKey] = (classTotals[periodKey] || 0) + duration;
  });

  const sortedClasses = [...totalsByClass.entries()]
    .sort((a, b) => {
      const totalA = Object.values(a[1]).reduce((sum, value) => sum + value, 0);
      const totalB = Object.values(b[1]).reduce((sum, value) => sum + value, 0);
      return totalB - totalA;
    });

  return {
    labels: periods.map(({ label }) => label),
    periodKeys,
    classes: sortedClasses,
  };
};

export const buildActivityTotals = (sessions, activityNamesById) => {
  const entries = Object.entries(sessions.reduce((acc, session) => {
    (session?.SessionActivity || []).forEach((activity) => {
      const duration = Number(activity.durationMinutes) || 0;
      if (duration <= 0) return;

      const activityName = getActivityName(activityNamesById, activity.activityId);
      acc[activityName] = (acc[activityName] || 0) + duration;
    });

    return acc;
  }, {})).sort((a, b) => b[1] - a[1]);

  const visibleEntries = entries.slice(0, ACTIVITY_DONUT_LIMIT);
  const otherDuration = entries
    .slice(ACTIVITY_DONUT_LIMIT)
    .reduce((total, [, duration]) => total + duration, 0);
  const groupedEntries = otherDuration > 0
    ? [...visibleEntries, ['All Others', otherDuration]]
    : visibleEntries;

  return {
    labels: groupedEntries.map(([activityName]) => activityName),
    values: groupedEntries.map(([, duration]) => duration),
  };
};

export const buildActivityOptions = (sessions, activityNamesById) =>
  Object.entries(sessions.reduce((acc, session) => {
    (session?.SessionActivity || []).forEach((activity) => {
      const duration = Number(activity.durationMinutes) || 0;
      if (duration <= 0) return;

      const activityId = String(activity.activityId);
      if (!acc[activityId]) {
        acc[activityId] = {
          id: activityId,
          name: getActivityName(activityNamesById, activity.activityId),
          duration: 0,
        };
      }

      acc[activityId].duration += duration;
    });

    return acc;
  }, {}))
    .map(([, activity]) => activity)
    .sort((a, b) => a.name.localeCompare(b.name));

export const buildClassOptions = (sessions, trainingClassesByName) => {
  const loggedClassDurations = sessions.reduce((acc, session) => {
    const duration = Number(session.durationMinutes) || 0;
    if (duration <= 0) return acc;

    const className = session.sessionClass || 'other';
    acc[className] = (acc[className] || 0) + duration;
    return acc;
  }, {});
  const classEntries = Object.keys(trainingClassesByName || {}).length > 0
    ? Object.keys(trainingClassesByName)
    : Object.keys(loggedClassDurations);

  return classEntries
    .map((className) => ({
      id: className,
      name: getTrainingClassLabel(trainingClassesByName, className),
      duration: loggedClassDurations[className] || 0,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const buildActivityDurationByPeriod = (sessions, period, selectedActivityId) => {
  const periods = createEmptyPeriods(sessions, period);
  const totalsByPeriod = sessions.reduce((acc, session) => {
    const parsedDate = parseSessionDate(session.sessionDate);
    if (!parsedDate) return acc;

    const periodKey = formatDateKey(getPeriodStart(parsedDate, period));
    (session?.SessionActivity || []).forEach((activity) => {
      if (String(activity.activityId) !== String(selectedActivityId)) return;

      const duration = Number(activity.durationMinutes) || 0;
      if (duration <= 0) return;

      acc[periodKey] = (acc[periodKey] || 0) + duration;
    });

    return acc;
  }, {});

  return {
    labels: periods.map(({ label }) => label),
    values: periods.map(({ key }) => totalsByPeriod[key] || 0),
  };
};

export const buildActivityClassTotals = (
  sessions,
  selectedActivityId,
  trainingClassesByName
) => {
  if (!selectedActivityId) {
    return {
      labels: [],
      selectedValues: [],
      otherValues: [],
    };
  }

  const totalsByClass = new Map();

  sessions.forEach((session) => {
    const sessionActivities = session?.SessionActivity || [];
    const selectedDuration = sessionActivities.reduce((total, activity) => (
      String(activity.activityId) === String(selectedActivityId)
        ? total + (Number(activity.durationMinutes) || 0)
        : total
    ), 0);
    const otherDuration = sessionActivities.reduce((total, activity) => (
      String(activity.activityId) !== String(selectedActivityId)
        ? total + (Number(activity.durationMinutes) || 0)
        : total
    ), 0);

    if (selectedDuration <= 0 && otherDuration <= 0) return;

    const sessionClass = session.sessionClass || 'other';
    const classLabel = getTrainingClassLabel(trainingClassesByName, sessionClass);

    if (!totalsByClass.has(classLabel)) {
      totalsByClass.set(classLabel, {
        selectedDuration: 0,
        otherDuration: 0,
      });
    }

    const classTotals = totalsByClass.get(classLabel);
    classTotals.selectedDuration += selectedDuration;
    classTotals.otherDuration += otherDuration;
  });

  const entries = [...totalsByClass.entries()]
    .filter(([, totals]) => totals.selectedDuration > 0)
    .sort((a, b) => (
      b[1].selectedDuration - a[1].selectedDuration
      || (b[1].selectedDuration + b[1].otherDuration)
      - (a[1].selectedDuration + a[1].otherDuration)
    ));

  return {
    labels: entries.map(([className]) => className),
    selectedValues: entries.map(([, totals]) => totals.selectedDuration),
    otherValues: entries.map(([, totals]) => totals.otherDuration),
  };
};

export const buildActivityStats = (sessions, selectedActivityId) => {
  const activitySessions = sessions.filter((session) => (
    (session?.SessionActivity || []).some((activity) => (
      String(activity.activityId) === String(selectedActivityId)
      && (Number(activity.durationMinutes) || 0) > 0
    ))
  ));
  const totalMinutes = activitySessions.reduce((sessionTotal, session) => (
    sessionTotal + (session?.SessionActivity || []).reduce((activityTotal, activity) => (
      String(activity.activityId) === String(selectedActivityId)
        ? activityTotal + (Number(activity.durationMinutes) || 0)
        : activityTotal
    ), 0)
  ), 0);
  const classCount = new Set(activitySessions.map((session) => session.sessionClass || 'other')).size;

  return {
    totalMinutes,
    sessionCount: activitySessions.length,
    averageMinutes: activitySessions.length > 0
      ? Math.round(totalMinutes / activitySessions.length)
      : 0,
    classCount,
  };
};

export const buildClassTrainingByPeriod = (sessions, period, selectedClassId) => {
  const periods = createEmptyPeriods(sessions, period);
  const totalsByPeriod = sessions.reduce((acc, session) => {
    if (String(session.sessionClass || 'other') !== String(selectedClassId)) return acc;

    const parsedDate = parseSessionDate(session.sessionDate);
    const duration = Number(session.durationMinutes) || 0;
    if (!parsedDate || duration <= 0) return acc;

    const periodKey = formatDateKey(getPeriodStart(parsedDate, period));
    acc[periodKey] = (acc[periodKey] || 0) + duration;
    return acc;
  }, {});

  return {
    labels: periods.map(({ label }) => label),
    values: periods.map(({ key }) => totalsByPeriod[key] || 0),
  };
};

export const buildClassActivityTotals = (sessions, selectedClassId, activityNamesById) => {
  const entries = Object.entries(sessions.reduce((acc, session) => {
    if (String(session.sessionClass || 'other') !== String(selectedClassId)) return acc;

    (session?.SessionActivity || []).forEach((activity) => {
      const duration = Number(activity.durationMinutes) || 0;
      if (duration <= 0) return;

      const activityName = getActivityName(activityNamesById, activity.activityId);
      acc[activityName] = (acc[activityName] || 0) + duration;
    });

    return acc;
  }, {})).sort((a, b) => b[1] - a[1]);

  const visibleEntries = entries.slice(0, ACTIVITY_DONUT_LIMIT);
  const otherDuration = entries
    .slice(ACTIVITY_DONUT_LIMIT)
    .reduce((total, [, duration]) => total + duration, 0);
  const groupedEntries = otherDuration > 0
    ? [...visibleEntries, ['All Others', otherDuration]]
    : visibleEntries;

  return {
    labels: groupedEntries.map(([activityName]) => activityName),
    values: groupedEntries.map(([, duration]) => duration),
  };
};

export const buildClassActivityTrainingByPeriod = (
  sessions,
  period,
  selectedClassId,
  activityNamesById
) => {
  const classSessions = sessions.filter((session) => (
    String(session.sessionClass || 'other') === String(selectedClassId)
  ));
  const periods = createEmptyPeriods(classSessions, period);
  const periodKeys = periods.map(({ key }) => key);
  const totalsByActivity = new Map();

  classSessions.forEach((session) => {
    const parsedDate = parseSessionDate(session.sessionDate);
    if (!parsedDate) return;

    const periodKey = formatDateKey(getPeriodStart(parsedDate, period));
    (session?.SessionActivity || []).forEach((activity) => {
      const duration = Number(activity.durationMinutes) || 0;
      if (duration <= 0) return;

      const activityName = getActivityName(activityNamesById, activity.activityId);
      if (!totalsByActivity.has(activityName)) {
        totalsByActivity.set(activityName, periodKeys.reduce((acc, key) => {
          acc[key] = 0;
          return acc;
        }, {}));
      }

      const activityTotals = totalsByActivity.get(activityName);
      activityTotals[periodKey] = (activityTotals[periodKey] || 0) + duration;
    });
  });

  const sortedActivities = [...totalsByActivity.entries()]
    .sort((a, b) => {
      const totalA = Object.values(a[1]).reduce((sum, value) => sum + value, 0);
      const totalB = Object.values(b[1]).reduce((sum, value) => sum + value, 0);
      return totalB - totalA;
    });

  return {
    labels: periods.map(({ label }) => label),
    periodKeys,
    activities: sortedActivities,
  };
};

export const buildClassStats = (sessions, selectedClassId) => {
  const classSessions = sessions.filter((session) => (
    String(session.sessionClass || 'other') === String(selectedClassId)
  ));
  const totalMinutes = classSessions.reduce((total, session) => (
    total + (Number(session.durationMinutes) || 0)
  ), 0);
  const uniqueActivityIds = classSessions.reduce((activityIds, session) => {
    (session?.SessionActivity || []).forEach((activity) => {
      if ((Number(activity.durationMinutes) || 0) > 0) {
        activityIds.add(activity.activityId);
      }
    });

    return activityIds;
  }, new Set());

  return {
    totalMinutes,
    sessionCount: classSessions.length,
    averageMinutes: classSessions.length > 0
      ? Math.round(totalMinutes / classSessions.length)
      : 0,
    activityCount: uniqueActivityIds.size,
  };
};

const getActivityCategory = (activityCategoriesById, activityId) =>
  activityCategoriesById?.[activityId] || 'Other';

export const buildActivityCategoryTrainingByPeriod = (sessions, period, activityCategoriesById) => {
  const periods = createEmptyPeriods(sessions, period);
  const periodKeys = periods.map(({ key }) => key);
  const totalsByCategory = new Map();

  sessions.forEach((session) => {
    const parsedDate = parseSessionDate(session.sessionDate);
    if (!parsedDate) return;

    const periodKey = formatDateKey(getPeriodStart(parsedDate, period));
    (session?.SessionActivity || []).forEach((activity) => {
      const duration = Number(activity.durationMinutes) || 0;
      if (duration <= 0) return;

      const categoryName = getActivityCategory(activityCategoriesById, activity.activityId);
      if (!totalsByCategory.has(categoryName)) {
        totalsByCategory.set(categoryName, periodKeys.reduce((acc, key) => {
          acc[key] = 0;
          return acc;
        }, {}));
      }

      const categoryTotals = totalsByCategory.get(categoryName);
      categoryTotals[periodKey] = (categoryTotals[periodKey] || 0) + duration;
    });
  });

  const sortedCategories = [...totalsByCategory.entries()]
    .sort((a, b) => {
      const totalA = Object.values(a[1]).reduce((sum, value) => sum + value, 0);
      const totalB = Object.values(b[1]).reduce((sum, value) => sum + value, 0);
      return totalB - totalA;
    });

  return {
    labels: periods.map(({ label }) => label),
    periodKeys,
    categories: sortedCategories,
  };
};
